import React from "react"
import type { IconName } from "../icon/assets/icon-map"
import { Icon } from "../icon"
import VisuallyHidden from "../visually-hidden"
import { useNavigationContext } from "./navigation-context"

// =============================================================================
// Props
// =============================================================================

export type NavigationDockTriggerProps = {
  /** Accessible label. Defaults to "Open navigation" / "Close navigation". */
  label?: string
}

// =============================================================================
// Styles
// =============================================================================

const triggerStyles = [
  "pointer-events-auto",
  "flex items-center justify-center shrink-0",
  "h-12 w-12 rounded-full",
  "bg-white ring-1 ring-inset ring-gray-200",
  "text-gray-700",
  "hover:bg-gray-100",
  "outline-2 outline-transparent -outline-offset-2 focus-visible:outline-gray-900",
  "transition-colors"
].join(" ")

// =============================================================================
// Component
// =============================================================================

/**
 * Toggles the shared sidebar open/closed state.
 * Must be rendered inside `Navigation` context.
 */
export function NavigationDockTrigger({ label }: NavigationDockTriggerProps) {
  const { collapsed, setCollapsed } = useNavigationContext()
  const defaultLabel = collapsed ? "Open navigation" : "Close navigation"
  const icon: IconName = collapsed ? "sidebar-right" : "close"

  return (
    <button
      type="button"
      data-slot="navigation-dock-trigger"
      aria-expanded={!collapsed}
      className={triggerStyles}
      onClick={() => setCollapsed(!collapsed)}
    >
      <VisuallyHidden>{label ?? defaultLabel}</VisuallyHidden>
      <Icon name={icon} aria-hidden />
    </button>
  )
}

NavigationDockTrigger.displayName = "Navigation.Dock.Trigger"
